import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { QRCodeCanvas } from "qrcode.react";
import { Plus, Trash, Monitor, SlidersHorizontal, Copy, ArrowRight, QrCode, X, Printer } from "@phosphor-icons/react";

export default function RingsTab({ eventId, event }) {
  const t = useT();
  const canEdit = ["owner", "co_organizer"].includes(event.my_role);
  const [rings, setRings] = useState([]);
  const [pools, setPools] = useState([]);
  const [matches, setMatches] = useState([]);
  const [form, setForm] = useState({ name: "Ring 1", discipline_filter: "both" });
  const [assignSel, setAssignSel] = useState({});
  const [qrRing, setQrRing] = useState(null);
  const allowedFilters = event?.type === "mixed" ? ["both", "combat", "poomsae"] : [event?.type].filter(Boolean);
  useEffect(() => {
    if (event?.type && event.type !== "mixed") setForm((f) => ({ ...f, discipline_filter: event.type }));
    // eslint-disable-next-line
  }, [event?.type]);
  const origin = window.location.origin;
  const secureUrl = (ring) => `${origin}/ring-login?event_id=${eventId}&ring_id=${ring.id}`;
  const quickUrl = (ring) => `${origin}/ring-login?token=${ring.quick_token}`;

  const load = useCallback(async () => {
    const [r, p, m] = await Promise.all([
      api.get(`/events/${eventId}/rings`),
      api.get(`/events/${eventId}/pools`),
      api.get(`/events/${eventId}/matches`),
    ]);
    setRings(r.data);
    setPools(p.data);
    setMatches(m.data);
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  const addRing = async () => {
    await api.post(`/events/${eventId}/rings`, { ...form, number: rings.length + 1 });
    setForm({ name: `Ring ${rings.length + 2}`, discipline_filter: event?.type && event.type !== "mixed" ? event.type : "both" });
    toast.success(t("ring.created"));
    load();
  };

  const removeRing = async (id) => {
    try { await api.delete(`/rings/${id}`); load(); }
    catch (e) { if (e.response?.status === 404) load(); else toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const assignPool = async (ring) => {
    const poolId = assignSel[ring.id];
    if (!poolId) return toast.error(t("ring.selectPool"));
    try {
      const { data } = await api.post(`/rings/${ring.id}/assign-pool`, { pool_id: poolId });
      toast.success(t("ring.assignedToast", { n: data.assigned, ring: ring.name }));
      setAssignSel((s) => ({ ...s, [ring.id]: "" }));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const unassignPool = async (ring, poolId) => {
    try {
      await api.delete(`/rings/${ring.id}/pools/${poolId}`);
      toast.success(t("ring.poolRemoved"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const printRing = (ring) => {
    const canvas = document.querySelector('[data-testid="ring-qr-canvas"]');
    const dataUrl = canvas ? canvas.toDataURL("image/png") : "";
    const pw = ring.password || event.ring_password || "";
    const url = quickUrl(ring);
    const w = window.open("", "_blank");
    if (!w) return;
    const d = w.document;
    const style = d.createElement("style");
    style.textContent = "@page{size:A4;margin:20mm;}body{font-family:Arial,sans-serif;text-align:center;color:#111;}h1{font-size:46px;margin:0 0 4px;}.num{font-size:20px;color:#555;margin-bottom:34px;}img{width:340px;height:340px;}.pw{margin-top:28px;font-size:22px;}.pw b{display:block;font-size:46px;letter-spacing:6px;font-family:monospace;margin-top:6px;}.url{margin-top:18px;font-size:12px;color:#666;word-break:break-all;}.hint{margin-top:30px;font-size:15px;color:#444;}";
    d.head.appendChild(style);
    d.title = ring.name;
    const mk = (tag, cls, text) => { const el = d.createElement(tag); if (cls) el.className = cls; if (text != null) el.textContent = text; return el; };
    d.body.appendChild(mk("h1", null, ring.name));
    d.body.appendChild(mk("div", "num", `Ring #${ring.number ?? ""}`));
    if (dataUrl) { const img = d.createElement("img"); img.src = dataUrl; d.body.appendChild(img); }
    const pwDiv = mk("div", "pw", `${t("ring.password")} `);
    pwDiv.appendChild(mk("b", null, pw));
    d.body.appendChild(pwDiv);
    d.body.appendChild(mk("div", "url", url));
    d.body.appendChild(mk("div", "hint", t("ring.scanStation")));
    setTimeout(() => { try { w.print(); } catch (e) { console.debug("print failed", e); } }, 400);
  };

  const openStation = (ring) => {
    window.open(`/ring-login?event_id=${eventId}&ring_id=${ring.id}`, "_blank");
  };

  const copy = async (txt) => { await copyToClipboard(txt); toast.success(t("ring.copied")); };

  const poolsWithMatches = pools.filter((p) => matches.some((m) => m.pool_id === p.id));
  const poolById = Object.fromEntries(pools.map((p) => [p.id, p]));
  const globallyAssignedPoolIds = new Set(matches.filter((m) => m.ring_id).map((m) => m.pool_id));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">{t("ring.title")}</h2>
      </div>

      {canEdit && <div className="border border-border rounded-lg p-4 bg-card flex flex-wrap items-end gap-3">
        <div className="space-y-1.5"><Label>{t("ring.name")}</Label><Input className="w-40" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ring-name-input" /></div>
        <div className="space-y-1.5">
          <Label>{t("ring.discipline")}</Label>
          <Select value={form.discipline_filter} onValueChange={(v) => setForm({ ...form, discipline_filter: v })} disabled={allowedFilters.length <= 1}>
            <SelectTrigger className="w-56" data-testid="ring-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>{allowedFilters.map((k) => <SelectItem key={k} value={k}>{t(`filter.${k}`)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={addRing} className="gap-2" data-testid="add-ring-btn"><Plus weight="bold" /> {t("ring.create")}</Button>
      </div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {rings.length === 0 && <p className="text-muted-foreground">{t("ring.none")}</p>}
        {rings.map((ring) => {
          const rMatches = matches.filter((m) => m.ring_id === ring.id);
          const assignedPoolIds = ((ring.pool_order && ring.pool_order.length)
            ? ring.pool_order
            : [...new Set(rMatches.map((m) => m.pool_id))]
          ).filter((pid) => poolById[pid]);
          const availablePools = poolsWithMatches.filter((p) => {
            if (globallyAssignedPoolIds.has(p.id)) return false;
            const f = ring.discipline_filter;
            if (!f || f === "both") return true;
            return p.discipline === f;
          });
          return (
            <div key={ring.id} className="border border-border rounded-lg p-5 bg-card" data-testid={`ring-item-${ring.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl font-bold">{ring.name}</h3>
                    <Badge className={`text-[10px] uppercase tracking-wider border-0 ${ring.discipline_filter === "combat" ? "bg-hong/20 text-hong" : ring.discipline_filter === "poomsae" ? "bg-chung/20 text-chung" : "bg-violet-500/20 text-violet-400"}`}>{t(`filter.${ring.discipline_filter}`)}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{t("ring.assigned", { n: rMatches.length })}</p>
                </div>
                {canEdit && <button onClick={() => removeRing(ring.id)} className="text-muted-foreground hover:text-hong" data-testid={`delete-ring-${ring.id}`}><Trash size={16} /></button>}
              </div>

              <div className="mt-4 flex justify-center">
                <div className="w-1/2 rounded-md bg-secondary/50 border border-border p-3 space-y-1.5 text-xs font-monot">
                  <div className="flex items-center justify-between"><span className="text-muted-foreground">{t("ring.eventId")}</span><button onClick={() => copy(eventId)} className="flex items-center gap-1 hover:text-white">{eventId.slice(0, 8)}… <Copy size={12} /></button></div>
                  <div className="flex items-center justify-between"><span className="text-muted-foreground">{t("ring.ringId")}</span><button onClick={() => copy(ring.id)} className="flex items-center gap-1 hover:text-white">{ring.id.slice(0, 8)}… <Copy size={12} /></button></div>
                  <div className="flex items-center justify-between"><span className="text-muted-foreground">{t("ring.password")}</span><button onClick={() => copy(ring.password || event.ring_password)} className="flex items-center gap-1 hover:text-white">{ring.password || event.ring_password} <Copy size={12} /></button></div>
                </div>
              </div>

              {canEdit && <div className="mt-4 flex justify-center">
                <div className="flex gap-2 w-1/2">
                  <Select value={assignSel[ring.id] || ""} onValueChange={(v) => setAssignSel({ ...assignSel, [ring.id]: v })}>
                    <SelectTrigger className="flex-1" data-testid={`assign-pool-select-${ring.id}`}><SelectValue placeholder={t("ring.assignPool")} /></SelectTrigger>
                    <SelectContent>
                      {poolsWithMatches.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">{t("ring.genFirst")}</div>}
                      {poolsWithMatches.length > 0 && availablePools.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">{t("ring.allPoolsAssigned")}</div>}
                      {availablePools.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.discipline === "combat" ? t("type.combat") : t("type.poomsae")})</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button variant="secondary" onClick={() => assignPool(ring)} data-testid={`assign-pool-btn-${ring.id}`}>{t("ring.assign")}</Button>
                </div>
              </div>}

              {assignedPoolIds.length > 0 && (
                <div className="mt-3" data-testid={`assigned-pools-${ring.id}`}>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1.5">{t("ring.assignedPools")}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {assignedPoolIds.map((pid, i) => (
                      <Badge key={pid} className="gap-1.5 pl-2 pr-1 py-1 text-xs bg-green-500/20 text-green-400 border-0" data-testid={`assigned-pool-${ring.id}-${pid}`}>
                        <span className="opacity-60">{i + 1}.</span> {poolById[pid]?.name || pid.slice(0, 6)}
                        <button onClick={() => unassignPool(ring, pid)} title={t("ring.removePool")} className={`hover:text-hong ml-0.5 ${!canEdit ? "hidden" : ""}`} data-testid={`remove-pool-${ring.id}-${pid}`}><X size={12} weight="bold" /></button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4 flex justify-center">
                <div className="flex gap-2 w-1/2">
                  <Button variant="outline" className="flex-1 gap-2" onClick={() => openStation(ring)} data-testid={`open-station-${ring.id}`}>
                    <ArrowRight /> {t("ring.openStation")}
                  </Button>
                  <Button variant="outline" size="icon" title={t("ring.accessQr")} onClick={() => setQrRing(ring)} data-testid={`ring-qr-btn-${ring.id}`}><QrCode /></Button>
                  <Button variant="outline" size="icon" title="Monitor" onClick={() => window.open(`/ring-login?event_id=${eventId}&ring_id=${ring.id}`, "_blank")}><Monitor /></Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!qrRing} onOpenChange={(o) => !o && setQrRing(null)}>
        <DialogContent data-testid="ring-qr-dialog">
          <DialogHeader>
            <DialogTitle>{qrRing?.name} — {t("ring.accessQr")}</DialogTitle>
            <DialogDescription>{t("ring.scanStation")}</DialogDescription>
          </DialogHeader>
          {qrRing && (
            <div className="space-y-4">
              <div className="flex justify-center bg-white rounded-lg p-4">
                <QRCodeCanvas value={quickUrl(qrRing)} size={200} data-testid="ring-qr-canvas" />
              </div>
              <button onClick={() => copy(quickUrl(qrRing))} className="w-full text-left text-xs font-monot bg-secondary border border-border rounded-md px-3 py-2 hover:border-brand-yellow flex items-center justify-between gap-2" data-testid="ring-quick-link">
                <span className="truncate">{t("ring.quickLink")}</span> <Copy size={13} className="shrink-0" />
              </button>
              <button onClick={() => copy(secureUrl(qrRing))} className="w-full text-left text-xs font-monot bg-secondary border border-border rounded-md px-3 py-2 hover:border-brand-yellow flex items-center justify-between gap-2" data-testid="ring-secure-link">
                <span className="truncate">{t("ring.secureLink")}</span> <Copy size={13} className="shrink-0" />
              </button>
              <p className="text-xs text-muted-foreground">{t("ring.password")}: <span className="font-monot text-foreground">{qrRing.password || event.ring_password}</span></p>
              <Button variant="secondary" className="w-full gap-2" onClick={() => printRing(qrRing)} data-testid="print-ring-btn"><Printer /> {t("ring.printRing")}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
